import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { APP_ROUTES } from "../constants";
import { useRouteWithToken } from "./useRouteWithToken";
import { useSessionStore } from "../store/useSessionStore";
import { RequestStatus } from "../utils/commons";
import { isClaimable, pendingOfacOperationId } from "../utils/claimGate";

/**
 * Redirects to ClaimBlocked when the deferred payout is no longer claimable
 * (bookmark / back-navigation guard). ClaimBlocked derives the message from
 * session.config (bindingState + senderCipStatus).
 */
export function useClaimableGuard() {
  const status = useSessionStore((s) => s.session.status);
  const bindingState = useSessionStore((s) => s.session.data?.config?.bindingState);
  const senderCipStatus = useSessionStore(
    (s) => s.session.data?.config?.senderCipStatus
  );
  const originationId = useSessionStore(
    (s) => s.session.data?.config?.originationId
  );
  const actionRequired = useSessionStore(
    (s) => s.session.data?.config?.actionRequired
  );
  const operationId = useSessionStore((s) => s.session.data?.config?.operationId);
  const { redirectTo } = useRouteWithToken();
  const { pathname } = useLocation();

  useEffect(() => {
    if (status !== RequestStatus.LOADED) return;
    if (!originationId) return;
    if (
      isClaimable({
        flow: "payout-claim",
        originationId,
        bindingState,
        senderCipStatus,
        actionRequired,
        operationId,
      })
    ) {
      return;
    }
    const ofacOperationId = pendingOfacOperationId({
      flow: "payout-claim",
      actionRequired,
      operationId,
    });
    if (ofacOperationId) {
      if (!pathname.includes(APP_ROUTES.OFAC_DISAMBIGUATION_PAGE)) {
        redirectTo(
          `${APP_ROUTES.OFAC_DISAMBIGUATION_PAGE}/${ofacOperationId}`,
          { replace: true }
        );
      }
      return;
    }
    if (pathname.includes(APP_ROUTES.CLAIM_BLOCKED_PAGE)) return;
    redirectTo(APP_ROUTES.CLAIM_BLOCKED_PAGE, { replace: true });
  }, [
    status,
    bindingState,
    senderCipStatus,
    originationId,
    actionRequired,
    operationId,
    pathname,
    redirectTo,
  ]);
}
